export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  platformRole: 'NONE' | 'OWNER' | 'ADMIN' | 'BILLING' | 'SUPPORT' | 'SECURITY' | 'AUDITOR';
  mustChangePassword: boolean;
}

export interface Membership {
  id: string;
  organization: { id: string; displayName: string; slug: string; status: string; workspaceUrl?: string; brandLogoUrl?: string | null; brandFaviconUrl?: string | null; brandPrimaryColor?: string; brandAccentColor?: string; brandLogoBrightness?: number; waitlistEnabled?: boolean };
  isOrganizationOwner: boolean;
  roles: Array<{ id?: string; code: string; name: string }>;
  permissions: string[];
  locations: Array<{ id: string; name: string; code: string }>;
}

export interface MeResponse {
  user: User;
  memberships: Membership[];
}

function cookie(name: string): string {
  const item = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return item ? decodeURIComponent(item.slice(name.length + 1)) : '';
}

export async function api<T>(path: string, options: RequestInit = {}, timeoutMs = 20_000): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = cookie('nuramend_csrf') || cookie('nuramend_staging_csrf') || cookie('nuramend_production_csrf');
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  const abortFromCaller = () => controller.abort();
  if (options.signal?.aborted) controller.abort();
  else options.signal?.addEventListener('abort', abortFromCaller, { once: true });

  try {
    const response = await fetch(`/api/v1${path}`, {
      ...options,
      method,
      headers,
      signal: controller.signal,
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('content-type') || '';
    const data: any = contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : { message: await response.text().catch(() => '') };

    if (!response.ok) {
      const message = typeof data.message === 'string' && data.message.trim()
        ? data.message
        : `Request failed with status ${response.status}.`;
      throw new Error(message);
    }
    return data as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('The request timed out. Please try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }
}
